import React, { useCallback, useMemo, useState } from "react";
import { EmployeeGroupCalculation } from "../../../hooks/reporting/useWorkTimeReportData";
import { WorkTimeReportFilters } from "../WorkTimeReportFilters";
import { useAppDispatch, useAppSelector } from "../../../framework/customStore";
import { GroupingValue } from "../../../store/reportOptionsSlice";
import { ReportGroupingPicker } from "../components/reportGroupingPicker";
import {
    ExpandLessButton,
    ReportButton,
    ReportSection,
} from "../ReportSection";
import { useTranslation } from "react-i18next";
import {
    ColumnGroup,
    ReportColumnPicker,
} from "../components/reportColumnPicker";
import { WorkTimeReportGridRow } from "../../../models/reporting/workTimeReport";
import { Base } from "../../../framework/base";
import { GridColDef } from "@mui/x-data-grid-premium";
import {
    workTimeReportSelectableOtherCategories,
    workTimeReportSelectablePaidCategories,
    workTimeReportSelectableUnpaidCategories,
} from "../../../models/workTime/hourBookingCategory";
import {
    ReportTemplateDeleteDto,
    ReportTemplateDto,
    DisplayMode,
    ReportTemplateSaveDto,
    ReportTemplateType,
} from "../../../models/reporting/reportTemplate";
import {
    reloadReportTemplates,
    setReportDisplayDecimals,
    setReportDisplayMode,
    setReportTemplateGroupByRow,
    setReportTemplateGrouping,
    setReportTemplateGroupingModeMultiple,
    setReportTemplateSelectedColumns,
    setReportTemplateShowGroupedRows,
    setSelectedReportTemplate,
} from "../../../store/reportTemplateSlice";
import { EmployeeListItemDto } from "../../../models/employee/employee";
import { SalaryRowTypeItem } from "../../../models/salary/salaryRowTypeItem";
import { employeeGroupListItemDto } from "../../../models/employeeGroup/employeeGroup";
import { SalaryPeriodTypeDto } from "../../../models/salaryPeriodType/salaryPeriodType";
import IconButton from "@mui/material/IconButton";
import SaveIcon from "@mui/icons-material/Save";
import SaveAsIcon from "@mui/icons-material/SaveAs";
import { ReportTemplateDialog } from "../ReportTemplateDialog";
import MuiMenu from "../../framework/muiMenu";
import {
    deleteReportTemplate,
    saveReportTemplate,
} from "../../../services/reportTemplateService";
import { ApiError } from "../../../services/apiClient";
import {
    showErrorMessage,
    showSuccessMessage,
} from "../../../models/store/storeActions";
import { Edit as EditIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { ConfirmationDialogType } from "../../../models/store/storeTypes";
import * as StoreActions from "../../../models/store/storeActions";
import { ReportDisplayOptions } from "../components/reportDisplayOptions";
import { BetaLabel } from "../../app/betaLabel";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Grid2 from "@mui/material/Unstable_Grid2";
import { setReportStopDurationThresholdMinutes } from "../../../store/reportTemplateSlice";

interface Props {
    employees: EmployeeListItemDto[];
    employeeGroups: employeeGroupListItemDto[];
    salaryRowTypes: SalaryRowTypeItem[];
    salaryPeriodTypes: SalaryPeriodTypeDto[];
    employeeGroupCalculations: EmployeeGroupCalculation[];
    reload: () => void;
    initialized: boolean;
    grouping: string[];
    groupingModel: (keyof WorkTimeReportGridRow)[];
    groupByRow: boolean;
    groupingModeMultiple: boolean;
    showGroupedRows: boolean;
    selectedColumns: string[];
    pinnedColumns: string[];
    gridDef: GridColDef<WorkTimeReportGridRow>[];
    type: ReportTemplateType;
    displayMode: DisplayMode;
    decimals: number;
    geofencesEnabled: boolean;
}

export const GroupableWorkTimeTopComponent = ({
    employees,
    employeeGroups,
    salaryRowTypes,
    salaryPeriodTypes,
    employeeGroupCalculations,
    reload,
    initialized,
    groupingModeMultiple,
    grouping,
    groupByRow,
    groupingModel,
    showGroupedRows,
    selectedColumns,
    pinnedColumns,
    gridDef,
    type,
    displayMode,
    decimals,
    geofencesEnabled,
}: Props) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const [expanded, setExpanded] = useState(false);
    const [saving, setSaving] = useState(false);
    const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
    const [templateEditDialogOpen, setTemplateEditDialogOpen] = useState(false);

    const templates = useAppSelector(
        (state) => state.reportTemplate.templates[type]
    );
    const templateId = useAppSelector(
        (state) => state.reportTemplate.selected[type]
    );
    const template = useMemo(
        () => templates?.find((t) => t.id === templateId),
        [templates, templateId]
    );

    const vehiclesByName = useAppSelector(
        (state) => state.reportOptions.vehicle.itemsByName
    );

    const workTimeTypesByName = useAppSelector(
        (state) => state.reportOptions.workTimeType.itemsByName
    );

    const customersByName = useAppSelector(
        (state) => state.reportOptions.customer.itemsByName
    );

    const costCenters = useAppSelector(
        (state) => state.reportOptions.costCenterOptions
    );

    const geofences = useAppSelector((state) => state.reportOptions.geofences);

    const stopThresholdMinutes = useAppSelector(
        (s) => s.reportTemplate.current.stopDurationThresholdMinutes
    );

    const handleThresholdChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const val = parseFloat(e.target.value);
            if (!isNaN(val) && val >= 0) {
                dispatch(setReportStopDurationThresholdMinutes(val));
            }
        },
        [dispatch]
    );

    const current = useAppSelector((state) => state.reportTemplate.current);
    const hasChanges = useAppSelector(
        (state) => state.reportTemplate.hasChanges
    );
    const timeRange = useAppSelector(
        (state) => state.reportTemplate.current.timeRange
    );

    const handleOpenTemplateDialog = () => setTemplateDialogOpen(true);
    const handleOpenTemplateEditDialog = () => setTemplateEditDialogOpen(true);

    const handleTemplateSelect = (id: string, type: ReportTemplateType) => {
        dispatch(setSelectedReportTemplate({ id, type }));
    };

    const handleSaveTemplate = async(
        isNew: boolean,
        dto?: Partial<ReportTemplateSaveDto>,
        isEdit?: boolean
    ) => {
        try {
            setSaving(true);

            const name = isEdit
                ? dto?.name
                : isNew
                    ? dto?.name
                    : template?.name;
            const json = isEdit
                ? JSON.stringify(template?.json)
                : JSON.stringify(current);

            const { id, type: templateType } = await saveReportTemplate({
                id: isNew ? null : template?.id,
                rowId: isNew ? null : template?.rowId,
                type: isNew ? type : template?.type,
                name: name,
                json: json,
            });

            dispatch(showSuccessMessage(t("common.saveSuccess")));
            dispatch(reloadReportTemplates());

            if (isNew) {
                // Set selected template if new template was created.
                handleTemplateSelect(id, templateType);
            }
            setSaving(false);

            return new Promise<void>((resolve) => resolve());
        } catch (error) {
            dispatch(
                showErrorMessage(
                    (error as ApiError)?.status === 409
                        ? t("common.saveConflict")
                        : t("common.saveFailed")
                )
            );
            setSaving(false);
            return new Promise<void>((_, reject) => reject());
        }
    };

    const handleDeleteTemplate = (template: ReportTemplateDto) => {
        dispatch(
            StoreActions.setConfirmation(
                ConfirmationDialogType.Warning,
                t("common.warning"),
                t("reportTemplate.deleteTemplateConfirmation", {
                    name: template.name,
                }),
                () => {
                    setSaving(true);
                    dispatch(StoreActions.clearConfirmation());

                    const data: ReportTemplateDeleteDto = {
                        rowId: template.rowId,
                    };

                    void deleteReportTemplate(template.id, data)
                        .then(() => {
                            dispatch(
                                showSuccessMessage(t("common.deleteSuccess"))
                            );
                            dispatch(reloadReportTemplates());
                            // Set empty/new template as selected after delete
                            handleTemplateSelect(null, null);
                        })
                        .catch((error) => {
                            dispatch(
                                showErrorMessage(
                                    (error as ApiError)?.status === 409
                                        ? t("common.deleteConflict")
                                        : t("common.deleteFailed")
                                )
                            );
                        })
                        .finally(void setSaving(false));
                },
                () => dispatch(StoreActions.clearConfirmation())
            )
        );
    };

    const groupingOptions = useMemo(
        () =>
            [
                GroupingValue.Employee,
                GroupingValue.Vehicle,
                GroupingValue.CostCenter,
                GroupingValue.Customer,
                GroupingValue.Date,
                GroupingValue.WorkTimeType,
                GroupingValue.WorkShift,
                GroupingValue.Geofence,
            ].filter((o) => geofencesEnabled || o !== GroupingValue.Geofence),
        [geofencesEnabled]
    );

    const columnPickerPinnedColumns = useMemo(
        () => [...groupingModel, ...pinnedColumns],
        [pinnedColumns, groupingModel]
    );

    const columnGroups: ColumnGroup[] = useMemo(() => {
        const getColumnsFromFields = (fields: string[], group?: string) =>
            fields
                ?.map((field: string) => {
                    const col = gridDef.find((c) => c.field === field);
                    if (!!col) {
                        return { id: col.field, title: col.headerName, group };
                    }
                })
                .filter(Boolean);

        return [
            {
                id: "common",
                label: t("reporting.common"),
                columns: getColumnsFromFields([
                    "duration",
                    "dateStr",
                    "workShiftStartTime",
                    "workShiftEndTime",
                ]),
            },
            {
                id: "workTimeData",
                label: t("reporting.workTimeData"),
                columns: getColumnsFromFields([
                    "employeeName",
                    "vehicleName",
                    "customerName",
                    "costCenterName",
                    "workTimeTypeName",
                    "comment",
                    "startTime",
                    "endTime",
                ]),
            },
            {
                id: "salaryRowTypes",
                label: t("reporting.salaryRowTypes"),
                columns: getColumnsFromFields(
                    salaryRowTypes?.map((t) => `srt_${t.id}`)
                ),
            },
            {
                id: "calculatedCategories",
                label: t("reporting.calculatedCategories"),
                columns: [
                    getColumnsFromFields(
                        workTimeReportSelectablePaidCategories().map(
                            (c) => `cat_${c}`
                        ),
                        t("reporting.paidCategories")
                    ),
                    getColumnsFromFields(
                        workTimeReportSelectableUnpaidCategories().map(
                            (c) => `cat_${c}`
                        ),
                        t("reporting.unpaidCategories")
                    ),
                    getColumnsFromFields(
                        workTimeReportSelectableOtherCategories().map(
                            (c) => `cat_${c}`
                        ),
                        t("reporting.otherCategories")
                    ),
                ].flat(),
                groupByEnabled: true,
            },
            columnPickerPinnedColumns.includes("employeeName")
                ? null
                : {
                    id: "employees",
                    label: t("reporting.employees"),
                    columns: getColumnsFromFields(
                        employees?.map((e) => `e_${e.id ?? "null"}`)
                    ),
                },
            columnPickerPinnedColumns.includes("vehicleName")
                ? null
                : {
                    id: "fleet",
                    label: t("reporting.fleet"),
                    columns: getColumnsFromFields(
                        Object.keys(vehiclesByName ?? {}).map(
                            (key) =>
                                  `v_${
                                      !vehiclesByName[key][0]?.id ? "null" : key
                                  }`
                        )
                    ),
                },
            columnPickerPinnedColumns.includes("customerName")
                ? null
                : {
                    id: "customers",
                    label: t("reporting.customers"),
                    columns: getColumnsFromFields(
                        Object.keys(customersByName ?? {}).map(
                            (key) =>
                                  `c_${
                                      !customersByName[key][0]?.id
                                          ? "null"
                                          : key
                                  }`
                        )
                    ),
                },
            columnPickerPinnedColumns.includes("costCenterName")
                ? null
                : {
                    id: "costCenters",
                    label: t("reporting.costCenters"),
                    columns: costCenters?.map(({ id, title, group }) => ({
                        id: `cc_${id ?? "null"}`,
                        title,
                        group,
                    })),
                    groupDropdownOptionsEnabled: true,
                },
            columnPickerPinnedColumns.includes("workTimeTypeName")
                ? null
                : {
                    id: "workTimeTypes",
                    label: t("reporting.workTimeTypes"),
                    columns: getColumnsFromFields(
                        Object.keys(workTimeTypesByName ?? {}).map(
                            (key) =>
                                  `wtt_${
                                      !workTimeTypesByName[key][0]
                                          ?.workTimeTypeId
                                          ? "null"
                                          : key
                                  }`
                        )
                    ),
                },
            {
                id: "fleetData",
                label: <BetaLabel betaTitle={t("reporting.fleetData")} />,
                columns: getColumnsFromFields([
                    "distance",
                    "fuel",
                    "consumption",
                    "emissions",
                ]),
            },
            geofencesEnabled && {
                id: "geofenceData",
                label: <BetaLabel betaTitle={t("reporting.geofences")} />,
                columns: getColumnsFromFields([
                    "geofenceName",
                    "geofenceEntries",
                    "geofenceDuration",
                    "geofenceDistance",
                    "geofenceStops",
                ]),
            },
            geofencesEnabled &&
            (geofences ?? []).length > 0 &&
            !columnPickerPinnedColumns.includes("geofenceName")
                ? {
                    id: "geofencesPerItem",
                    label: <BetaLabel betaTitle={t("reporting.geofencesPerItem")} />,
                    columns: (geofences ?? []).flatMap((g) => [
                        {
                            id: `gf_entries_${g.geofenceId}`,
                            title: `${g.name} - ${t("reporting.geofenceEntries")}`,
                        },
                        {
                            id: `gf_duration_${g.geofenceId}`,
                            title: `${g.name} - ${t("reporting.geofenceDuration")}`,
                        },
                        {
                            id: `gf_distance_${g.geofenceId}`,
                            title: `${g.name} - ${t("reporting.geofenceDistance")}`,
                        },
                        {
                            id: `gf_stops_${g.geofenceId}`,
                            title: `${g.name} - ${t("reporting.geofenceStops")}`,
                        },
                    ]),
                }
                : null,
            geofencesEnabled && {
                id: "vehicleGeofenceData",
                label: <BetaLabel betaTitle={t("reporting.vehicleGeofences")} />,
                columns: getColumnsFromFields([
                    "vehicleGeofenceEntries",
                    "vehicleGeofenceDuration",
                    "vehicleGeofenceDistance",
                    "vehicleGeofenceStops",
                ]),
            },
        ].filter(Boolean);
    }, [
        gridDef,
        salaryRowTypes,
        workTimeTypesByName,
        employees,
        vehiclesByName,
        costCenters,
        customersByName,
        geofences,
        geofencesEnabled,
        columnPickerPinnedColumns,
        t,
    ]);

    const showTemplateSelect =
        templates?.length > 1 || (templates?.length > 0 && !template);

    const reportDateLabel = useMemo(
        () =>
            `${Base.dayjsToDateStr(timeRange[0])} - ${Base.dayjsToDateStr(
                timeRange[1]
            )}`,
        [timeRange[0], timeRange[1]]
    );

    return initialized ? (
        <>
            <ReportSection
                id="work-time-report-top-section"
                header={template?.name ?? t("reportTemplate.newReport")}
                topDivider
                subtitle={!expanded ? reportDateLabel : null}
                headerAction={
                    !expanded ? null : !template?.id ? (
                        <IconButton
                            disabled={saving}
                            color="primary"
                            size="large"
                            sx={{ ml: 1 }}
                            onClick={handleOpenTemplateDialog}
                        >
                            <SaveIcon />
                        </IconButton>
                    ) : (
                        <>
                            <MuiMenu
                                disabled={saving || !hasChanges}
                                buttonSx={{ ml: 1 }}
                                noBox
                                buttonColor="primary"
                                size="large"
                                label={<SaveIcon />}
                                items={[
                                    {
                                        label: t("common.saveChanges"),
                                        icon: <SaveIcon color="primary" />,
                                        onClick: () =>
                                            void handleSaveTemplate(false),
                                    },
                                    {
                                        label: t(
                                            "reportTemplate.saveAsReportTemplate"
                                        ),
                                        icon: <SaveAsIcon color="primary" />,
                                        onClick: handleOpenTemplateDialog,
                                    },
                                ]}
                            />
                            <IconButton
                                disabled={saving || hasChanges}
                                color="primary"
                                size="large"
                                onClick={handleOpenTemplateEditDialog}
                            >
                                <EditIcon />
                            </IconButton>
                            <IconButton
                                disabled={saving}
                                color="error"
                                size="large"
                                onClick={() => handleDeleteTemplate(template)}
                            >
                                <DeleteIcon />
                            </IconButton>
                        </>
                    )
                }
                headerRight={
                    expanded && showTemplateSelect ? (
                        <MuiMenu
                            label={t("reportTemplate.changeTemplate")}
                            buttonColor="success"
                            buttonVariant="outlined"
                            buttonSx={{ height: 40 }}
                            iconButton={false}
                            items={templates.map((t) => ({
                                label: t.name,
                                disabled: t.id === templateId,
                                onClick: () =>
                                    handleTemplateSelect(t.id, t.type),
                            }))}
                        />
                    ) : null
                }
                headerProps={{
                    variant: "h2",
                    color: "green.dark",
                }}
                expanded={expanded}
                setExpanded={setExpanded}
                showExpandToggle={!expanded}
                sx={{
                    overflow: "auto",
                    overflowX: "hidden",
                }}
                subsections={[
                    {
                        id: "filters",
                        header: t("reporting.filters"),
                        action: (
                            <ExpandLessButton
                                sx={{ mb: 1 }}
                                onClick={() => setExpanded(false)}
                            />
                        ),
                        content: (
                            <WorkTimeReportFilters
                                employees={employees}
                                employeeGroups={employeeGroups}
                                salaryPeriodTypes={salaryPeriodTypes}
                                employeeGroupCalculations={
                                    employeeGroupCalculations
                                }
                                reload={reload}
                                geofencesEnabled={geofencesEnabled}
                            />
                        ),
                    },
                    ...(geofencesEnabled
                        ? [
                            {
                                id: "searchParameters",
                                header: t("reporting.searchParameters"),
                                content: (
                                    <Grid2
                                        container
                                        spacing={2}
                                        alignItems="center"
                                    >
                                        <Grid2 xs="auto">
                                            <TextField
                                                id="report-geofence-stop-threshold"
                                                label={t(
                                                    "monitorView.geofence.stopThreshold"
                                                )}
                                                type="number"
                                                size="small"
                                                value={stopThresholdMinutes}
                                                onChange={
                                                      handleThresholdChange
                                                  }
                                                inputProps={{
                                                    min: 0,
                                                    step: 0.5,
                                                }}
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            {t(
                                                                "monitorView.geofence.stopThresholdUnit"
                                                            )}
                                                        </InputAdornment>
                                                    ),
                                                }}
                                                sx={{ width: 180 }}
                                            />
                                        </Grid2>
                                    </Grid2>
                                ),
                            },
                        ]
                        : []),
                    {
                        // Grouping selectors
                        id: "grouping",
                        header: t("reporting.groupBy"),
                        content: (
                            <ReportGroupingPicker
                                selectedGrouping={grouping}
                                groupByRow={groupByRow}
                                options={groupingOptions}
                                setGrouping={setReportTemplateGrouping}
                                setGroupByRow={setReportTemplateGroupByRow}
                                groupingModeMultiple={groupingModeMultiple}
                                enableGroupingModeSelection
                                setGroupingModeMultiple={
                                    setReportTemplateGroupingModeMultiple
                                }
                                enableShowGroupedRowsSelection
                                showGroupedRows={showGroupedRows}
                                setShowGroupedRows={
                                    setReportTemplateShowGroupedRows
                                }
                            />
                        ),
                    },
                    {
                        id: "columns",
                        header: t("reporting.columnSelection"),
                        subtitle: t("reporting.columnOrderInfo"),
                        content: (
                            <ReportColumnPicker
                                columnGroups={columnGroups}
                                selectedColumns={selectedColumns}
                                setSelectedColumns={
                                    setReportTemplateSelectedColumns
                                }
                                pinnedColumns={columnPickerPinnedColumns}
                            />
                        ),
                        action: (
                            <ReportButton
                                onClick={() =>
                                    dispatch(
                                        setReportTemplateSelectedColumns([])
                                    )
                                }
                            >
                                {t("common.clear")}
                            </ReportButton>
                        ),
                    },
                    {
                        id: "display",
                        header: t("reporting.displayOptions"),
                        content: (
                            <ReportDisplayOptions
                                mode={displayMode}
                                decimals={decimals}
                                setDisplayMode={setReportDisplayMode}
                                setDecimals={setReportDisplayDecimals}
                            />
                        ),
                    },
                ]}
            />
            <ReportTemplateDialog
                template={template}
                open={templateDialogOpen}
                onClose={() => setTemplateDialogOpen(false)}
                onSubmit={(dto) => handleSaveTemplate(true, dto)}
            />
            <ReportTemplateDialog
                template={template}
                open={templateEditDialogOpen}
                onClose={() => setTemplateEditDialogOpen(false)}
                onSubmit={(dto) => handleSaveTemplate(false, dto, true)}
                isEdit
            />
        </>
    ) : null;
};
